import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  Legend,
} from "recharts";
import { formatPrice } from "@/utils/formatPrice";
import { toast } from "sonner";
import { Calculator, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import ExportDropdown from "./ExportDropdown";
import { excludeMatchiPayments } from "@/lib/matchiRevenue";

const CATEGORY_KEYS = [
  { key: "revenue_booking", i18nKey: "courtBookings" },
  { key: "revenue_membership", i18nKey: "memberships" },
  { key: "revenue_training", i18nKey: "training" },
  { key: "revenue_tournament", i18nKey: "tournaments" },
  { key: "revenue_other", i18nKey: "other" },
] as const;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Maj",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dec",
];

type CategoryKey = (typeof CATEGORY_KEYS)[number]["key"];

function categorizePaymentToCategory(paymentType: string | null): CategoryKey {
  const t = (paymentType || "").toLowerCase();
  if (t.includes("tournament")) return "revenue_tournament";
  if (t.includes("training") || t.includes("lesson")) return "revenue_training";
  if (t.includes("member")) return "revenue_membership";
  return "revenue_booking";
}

export default function BudgetTab() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();

  const CATEGORIES = CATEGORY_KEYS.map((c) => ({
    key: c.key,
    label: t(`reports.budget.categories.${c.i18nKey}`),
  }));
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());

  // Local state for the grid
  // grid shape: { "revenue_booking": { 1: 80000, 2: 85000, ... }, ... }
  const [grid, setGrid] = useState<Record<string, Record<number, number>>>({});

  // Refs for debounced saves
  const saveTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ---------- Queries ----------

  const { data: budgetEntries = [] } = useQuery({
    queryKey: ["budget-entries", organization?.id, year],
    queryFn: async () => {
      const { data } = await supabase
        .from("budget_entries" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("year", year);
      return (data ?? []) as Array<{
        category: string;
        month: number;
        amount: number;
      }>;
    },
    enabled: !!organization?.id,
  });

  // Initialize grid from DB data
  useEffect(() => {
    const newGrid: Record<string, Record<number, number>> = {};
    for (const entry of budgetEntries) {
      if (!newGrid[entry.category]) newGrid[entry.category] = {};
      newGrid[entry.category][entry.month] = Number(entry.amount) || 0;
    }
    setGrid(newGrid);
  }, [budgetEntries]);

  // Fetch actual revenue per month (for comparison chart)
  const { data: actualPayments = [] } = useQuery({
    queryKey: ["budget-actuals", organization?.id, year],
    queryFn: async () => {
      const from = `${year}-01-01`;
      const to = `${year}-12-31`;
      const { data } = await supabase
        .from("booking_payments")
        .select("amount, payment_type, created_at, payment_method, payment_method_id")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", from)
        .lte("created_at", to);
      // C3/BES-004: budgeten är Boplys — MATCHi-avräknade betalningar hör inte hemma
      // i utfallet (och får definitivt inte kopieras in som nästa års budget).
      return excludeMatchiPayments((data ?? []) as Array<{
        amount: number;
        payment_type: string | null;
        created_at: string;
        payment_method: string | null;
        payment_method_id: string | null;
      }>);
    },
    enabled: !!organization?.id,
  });

  // Fetch previous year actuals for copy feature
  const { data: prevYearPayments = [] } = useQuery({
    queryKey: ["budget-actuals", organization?.id, year - 1],
    queryFn: async () => {
      const from = `${year - 1}-01-01`;
      const to = `${year - 1}-12-31`;
      const { data } = await supabase
        .from("booking_payments")
        .select("amount, payment_type, created_at, payment_method, payment_method_id")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", from)
        .lte("created_at", to);
      // C3/BES-004: budgeten är Boplys — MATCHi-avräknade betalningar hör inte hemma
      // i utfallet (och får definitivt inte kopieras in som nästa års budget).
      return excludeMatchiPayments((data ?? []) as Array<{
        amount: number;
        payment_type: string | null;
        created_at: string;
        payment_method: string | null;
        payment_method_id: string | null;
      }>);
    },
    enabled: !!organization?.id,
  });

  // ---------- Derived data ----------

  const monthlyActuals = useMemo(() => {
    const result: Record<number, number> = {};
    for (const p of actualPayments) {
      const month = new Date(p.created_at).getMonth() + 1;
      result[month] = (result[month] || 0) + (p.amount || 0);
    }
    return result;
  }, [actualPayments]);

  // ---------- Save mutation ----------

  const saveMutation = useMutation({
    mutationFn: async ({
      category,
      month,
      amount,
    }: {
      category: string;
      month: number;
      amount: number;
    }) => {
      const { error } = await supabase.from("budget_entries" as any).upsert(
        {
          organization_id: organization!.id,
          year,
          month,
          category,
          amount,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "organization_id,year,month,category" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budget-entries"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ---------- Cell change handler with debounce ----------

  const handleCellChange = useCallback(
    (category: string, month: number, value: string) => {
      const amount = parseInt(value) || 0;
      setGrid((prev) => ({
        ...prev,
        [category]: { ...(prev[category] || {}), [month]: amount },
      }));

      const key = `${category}-${month}`;
      if (saveTimeouts.current[key]) {
        clearTimeout(saveTimeouts.current[key]);
      }
      saveTimeouts.current[key] = setTimeout(() => {
        saveMutation.mutate({ category, month, amount });
        delete saveTimeouts.current[key];
      }, 1000);
    },
    [saveMutation]
  );

  // ---------- Copy last year's actuals ----------

  const copyLastYear = useCallback(() => {
    const newGrid: Record<string, Record<number, number>> = {};
    for (const cat of CATEGORIES) {
      newGrid[cat.key] = {};
    }

    for (const p of prevYearPayments) {
      const month = new Date(p.created_at).getMonth() + 1;
      const cat = categorizePaymentToCategory(p.payment_type);
      newGrid[cat][month] = (newGrid[cat][month] || 0) + (p.amount || 0);
    }

    // Round values
    for (const cat of CATEGORIES) {
      for (let m = 1; m <= 12; m++) {
        if (newGrid[cat.key][m]) {
          newGrid[cat.key][m] = Math.round(newGrid[cat.key][m]);
        }
      }
    }

    setGrid(newGrid);

    // Save all cells
    for (const cat of CATEGORIES) {
      for (let m = 1; m <= 12; m++) {
        const amount = newGrid[cat.key][m] || 0;
        saveMutation.mutate({ category: cat.key, month: m, amount });
      }
    }

    toast.success(t("reports.budget.lastYearCopied"));
  }, [prevYearPayments, saveMutation]);

  // ---------- Forecast ----------

  const forecast = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const dayOfMonth = now.getDate();

    return MONTHS.map((_, i) => {
      const month = i + 1;
      const actual = monthlyActuals[month] || 0;
      const daysInMonth = new Date(year, month, 0).getDate();

      if (year < now.getFullYear() || month < currentMonth) {
        return actual;
      }

      if (month === currentMonth && year === now.getFullYear()) {
        return dayOfMonth > 0
          ? Math.round((actual / dayOfMonth) * daysInMonth)
          : 0;
      }

      // Future months: use YTD average daily rate
      const ytdTotal = Object.entries(monthlyActuals).reduce(
        (s, [m, v]) => (Number(m) < currentMonth ? s + v : s),
        0
      );
      const startOfYear = new Date(year, 0, 1);
      const daysPassed = Math.max(
        1,
        Math.floor(
          (now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)
        )
      );
      return Math.round((ytdTotal / daysPassed) * daysInMonth);
    });
  }, [monthlyActuals, year]);

  // ---------- Totals ----------

  const grandTotal = useMemo(
    () =>
      CATEGORIES.reduce(
        (s, c) =>
          s +
          Object.values(grid[c.key] || {}).reduce(
            (ss, v) => ss + (v || 0),
            0
          ),
        0
      ),
    [grid]
  );

  const currentMonth = new Date().getMonth() + 1;
  const isCurrentYear = year === new Date().getFullYear();

  const budgetYtd = useMemo(() => {
    const upToMonth = isCurrentYear ? currentMonth : 12;
    return CATEGORIES.reduce((s, c) => {
      for (let m = 1; m <= upToMonth; m++) {
        s += grid[c.key]?.[m] || 0;
      }
      return s;
    }, 0);
  }, [grid, currentMonth, isCurrentYear]);

  const actualYtd = useMemo(() => {
    const upToMonth = isCurrentYear ? currentMonth : 12;
    let total = 0;
    for (let m = 1; m <= upToMonth; m++) {
      total += monthlyActuals[m] || 0;
    }
    return total;
  }, [monthlyActuals, currentMonth, isCurrentYear]);

  const variance = actualYtd - budgetYtd;

  // ---------- Chart data ----------

  const chartData = useMemo(
    () =>
      MONTHS.map((m, i) => ({
        month: m,
        budget: CATEGORIES.reduce(
          (s, c) => s + (grid[c.key]?.[i + 1] || 0),
          0
        ),
        utfall: monthlyActuals[i + 1] || 0,
        prognos: forecast[i],
      })),
    [grid, monthlyActuals, forecast]
  );

  // ---------- Export ----------

  const exportData = useCallback(
    () =>
      CATEGORIES.flatMap((cat) =>
        MONTHS.map((m, i) => ({
          [t("reports.budget.category")]: cat.label,
          [t("reports.budget.monthLabel")]: m,
          [t("reports.budget.budget")]: grid[cat.key]?.[i + 1] || 0,
          [t("reports.budget.actual")]: monthlyActuals[i + 1] || 0,
        }))
      ),
    [grid, monthlyActuals]
  );

  // ---------- Render ----------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          <span className="text-sm font-medium">{t("reports.budget.title")}</span>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[year - 1, year, year + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyLastYear}
            className="gap-1.5"
          >
            <Copy className="h-3.5 w-3.5" /> {t("reports.budget.copyLastYear")}
          </Button>
          <ExportDropdown getData={exportData} filePrefix={`budget_${year}`} />
        </div>
      </div>

      {/* Editable grid */}
      <Card>
        <CardContent className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                  {t("reports.budget.category")}
                </th>
                {MONTHS.map((m) => (
                  <th
                    key={m}
                    className="text-right px-1 py-2 font-medium text-muted-foreground min-w-[70px]"
                  >
                    {m}
                  </th>
                ))}
                <th className="text-right pl-3 py-2 font-bold">{t("reports.budget.total")}</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((cat) => {
                const rowTotal = Object.values(grid[cat.key] || {}).reduce(
                  (s, v) => s + (v || 0),
                  0
                );
                return (
                  <tr key={cat.key} className="border-b border-border/50">
                    <td className="py-1.5 pr-4 font-medium">{cat.label}</td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="px-1 py-1">
                        <Input type="number"
                          className="h-7 text-right text-xs w-[70px]"
                          value={grid[cat.key]?.[i + 1] ?? ""}
                          onChange={(e) =>
                            handleCellChange(cat.key, i + 1, e.target.value)
                          }
                        />
                      </td>
                    ))}
                    <td className="text-right pl-3 font-bold">
                      {formatPrice(rowTotal)}
                    </td>
                  </tr>
                );
              })}
              {/* TOTALT row */}
              <tr className="font-bold border-t-2">
                <td className="py-2 pr-4">{t("reports.budget.total").toUpperCase()}</td>
                {MONTHS.map((_, i) => {
                  const monthTotal = CATEGORIES.reduce(
                    (s, c) => s + (grid[c.key]?.[i + 1] || 0),
                    0
                  );
                  return (
                    <td key={i} className="text-right px-1 py-2">
                      {formatPrice(monthTotal)}
                    </td>
                  );
                })}
                <td className="text-right pl-3">{formatPrice(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Budget vs Utfall chart */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm">{t("reports.budget.budgetVsActual")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-border"
              />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <Tooltip formatter={(v: number) => formatPrice(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="budget"
                fill="hsl(var(--primary) / 0.3)"
                stroke="hsl(var(--primary))"
                strokeWidth={1}
                name={t("reports.budget.budget")}
              />
              <Bar
                dataKey="utfall"
                fill="hsl(var(--primary))"
                name={t("reports.budget.actual")}
                radius={[4, 4, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="prognos"
                stroke="hsl(var(--chart-3))"
                strokeDasharray="5 5"
                strokeWidth={2}
                dot={false}
                name={t("reports.budget.forecast")}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Variance summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.budget.budgetYtd")}</p>
            <p className="text-2xl font-bold">{formatPrice(budgetYtd)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.budget.actualYtd")}</p>
            <p className="text-2xl font-bold">{formatPrice(actualYtd)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.budget.variance")}</p>
            <p
              className={cn(
                "text-2xl font-bold",
                variance >= 0 ? "text-green-500" : "text-destructive"
              )}
            >
              {variance >= 0 ? "+" : ""}
              {formatPrice(variance)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
